/**
 * What the arena is made of, and what happens when somebody arrives at it fast.
 *
 * There are two shapes, and the difference between them is which side of the
 * surface the arena is on.
 *
 *   `plane`   a flat face on the floor plane, declared as a segment plus the
 *             direction that points **into** the arena. A boundary: you stay
 *             on one side of it. This is the default, and every wall is one.
 *   `column`  a circle on the floor plane, declared as a centre and a radius.
 *             An obstacle: you stay *outside* it, and the direction out is
 *             wherever you happen to be standing. A subway pillar is one.
 *   `box`     an axis-aligned rectangle on the floor plane. The same kind of
 *             thing as a column, with corners. A train carriage is one, and
 *             unlike everything else here it **moves** — nothing in this module
 *             assumes a surface holds still, because nothing needed to until
 *             one did.
 *
 * Both answer the same question — how deep is this body into that thing — so
 * the collision does not care which it is holding, and neither does the hit
 * that comes out of it. A fighter is a circle either way.
 *
 * Tagging is the point. `hard: true` is what makes a surface something you can
 * be put through; anything else is scenery, and a fighter thrown at it just
 * stops. Which means an arena can have ring ropes that give and turnbuckles that
 * do not, and neither of them is code.
 *
 * Pure, and deliberately not three.js. `src/arenas/` builds the geometry that
 * matches this data; this module never sees a mesh.
 */

export class SurfaceConfigError extends Error {}

/** The two things an arena can be made of. See the header. */
export const SHAPES = Object.freeze(['plane', 'column', 'box']);

/** Shapes you stay outside of, rather than one side of. */
const SOLIDS = Object.freeze(['column', 'box']);

/** A surface with no `shape` is a plane, which is what every arena had first. */
export function shapeOf(surface) {
  return surface?.shape ?? 'plane';
}

/**
 * The plane a surface lies on, as `n·p + d = 0`, with `n·p + d` reading as the
 * signed distance *into* the arena from it.
 *
 * Derived from the segment rather than stored, so a surface cannot be edited
 * into disagreeing with itself.
 */
export function planeOf(surface) {
  const { normal, from } = surface;
  return -(normal.x * from.x + normal.z * from.z);
}

/**
 * How far inside the arena a point is, measured from one surface.
 *
 * For a column that is the gap to its side rather than to its centre, so the
 * number means the same thing it does for a wall and `contact` can compare the
 * two without knowing which it has.
 */
export function depthFrom(point, surface) {
  const shape = shapeOf(surface);
  if (shape === 'column') {
    const { center, radius } = surface;
    return Math.hypot(point.x - center.x, point.z - center.z) - radius;
  }
  if (shape === 'box') {
    const { center, halfX, halfZ } = surface;
    const dx = Math.abs(point.x - center.x) - halfX;
    const dz = Math.abs(point.z - center.z) - halfZ;
    // Outside, the gap is the distance to the nearest corner or face. Inside,
    // both are negative and the larger one is the shallowest way out — which
    // is the one a body gets pushed along.
    if (dx > 0 || dz > 0) return Math.hypot(Math.max(dx, 0), Math.max(dz, 0));
    return Math.max(dx, dz);
  }
  return surface.normal.x * point.x + surface.normal.z * point.z + planeOf(surface);
}

/**
 * The direction out of a surface at a point — where a body resting against it
 * gets pushed, and which way it comes off when it arrives fast.
 *
 * A wall's is stored, because a wall faces one way wherever you stand. A
 * column's is not: it is radial, and every point around it has its own. A body
 * exactly on a column's axis has no direction out at all, and is handed an
 * arbitrary one rather than a NaN — an impossible position should not be able
 * to poison a position that is merely wrong.
 */
const AXIS_FALLBACK = Object.freeze({ x: 1, z: 0 });

export function normalAt(point, surface) {
  const shape = shapeOf(surface);
  if (shape === 'column') {
    const dx = point.x - surface.center.x;
    const dz = point.z - surface.center.z;
    const length = Math.hypot(dx, dz);
    if (length < 1e-9) return AXIS_FALLBACK;
    return { x: dx / length, z: dz / length };
  }
  if (shape === 'box') {
    const { center, halfX, halfZ } = surface;
    const offsetX = point.x - center.x;
    const offsetZ = point.z - center.z;
    const dx = Math.abs(offsetX) - halfX;
    const dz = Math.abs(offsetZ) - halfZ;
    if (dx > 0 || dz > 0) {
      // Outside: away from the nearest point on the rectangle.
      const outX = Math.max(dx, 0) * Math.sign(offsetX || 1);
      const outZ = Math.max(dz, 0) * Math.sign(offsetZ || 1);
      const length = Math.hypot(outX, outZ);
      if (length < 1e-9) return AXIS_FALLBACK;
      return { x: outX / length, z: outZ / length };
    }
    // Inside: out through the nearest face. A body that has ended up under a
    // train is shoved clear along the short way, not the long way.
    if (dx > dz) return { x: Math.sign(offsetX || 1), z: 0 };
    return { x: 0, z: Math.sign(offsetZ || 1) };
  }
  return surface.normal;
}

/** Throws on arena data that cannot be collided against, so a typo fails at boot. */
export function validateSurfaces(surfaces) {
  if (!Array.isArray(surfaces) || surfaces.length === 0) {
    throw new SurfaceConfigError('an arena needs at least one surface');
  }
  const seen = new Set();
  for (const surface of surfaces) {
    const { id, normal, from, to } = surface;
    if (!id) throw new SurfaceConfigError('a surface has no id');
    if (seen.has(id)) throw new SurfaceConfigError(`two surfaces share the id "${id}"`);
    seen.add(id);

    const shape = shapeOf(surface);
    if (!SHAPES.includes(shape)) {
      throw new SurfaceConfigError(`surface "${id}" is a "${shape}"; expected ${SHAPES.join(' or ')}`);
    }

    if (SOLIDS.includes(shape)) {
      const { center } = surface;
      if (!center || !Number.isFinite(center.x) || !Number.isFinite(center.z)) {
        throw new SurfaceConfigError(`${shape} "${id}" has a bad center`);
      }
      if (shape === 'column' && !(surface.radius > 0)) {
        throw new SurfaceConfigError(`column "${id}" needs a positive radius`);
      }
      if (shape === 'box' && !(surface.halfX > 0 && surface.halfZ > 0)) {
        throw new SurfaceConfigError(`box "${id}" needs a positive halfX and halfZ`);
      }
      if (surface.hard && !(surface.damage > 0)) {
        throw new SurfaceConfigError(`hard surface "${id}" must do positive damage`);
      }
      continue;
    }

    for (const [field, point] of [['normal', normal], ['from', from], ['to', to]]) {
      if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.z)) {
        throw new SurfaceConfigError(`surface "${id}" has a bad ${field}`);
      }
    }
    const length = Math.hypot(normal.x, normal.z);
    if (Math.abs(length - 1) > 1e-6) {
      throw new SurfaceConfigError(`surface "${id}" has a normal of length ${length}; want 1`);
    }
    if (Math.hypot(to.x - from.x, to.z - from.z) < 1e-6) {
      throw new SurfaceConfigError(`surface "${id}" is a point, not a face`);
    }
    // The normal has to be perpendicular to the face, or "how deep into the
    // arena" means nothing.
    const along = { x: to.x - from.x, z: to.z - from.z };
    const alongLength = Math.hypot(along.x, along.z);
    const skew = Math.abs((normal.x * along.x + normal.z * along.z) / alongLength);
    if (skew > 1e-6) {
      throw new SurfaceConfigError(`surface "${id}" has a normal that is not square to it`);
    }
    if (surface.hard && !(surface.damage > 0)) {
      throw new SurfaceConfigError(`hard surface "${id}" must do positive damage`);
    }
  }
  return surfaces;
}

/**
 * The hard surface a body of `radius` at `point` is furthest into, or null.
 *
 * Deepest wins, the same way the deepest hurtbox wins a strike: a fighter
 * thrown into a corner is touching two walls, and the one they are further into
 * is the one they hit.
 *
 * A `plane` is treated as an infinite plane rather than as its segment, which
 * is exactly right for a convex arena boundary and would need rethinking for a
 * concave one. The segment is still declared, because it is what the geometry
 * is built from. A `column` and a `box` are bounded by construction, which is
 * why pillars standing in the middle of an arena are a column and not four very
 * short walls.
 */
export function contact(point, radius, surfaces) {
  let worst = null;
  for (const surface of surfaces ?? []) {
    if (!surface.hard) continue;
    const depth = depthFrom(point, surface);
    if (depth >= radius) continue;
    const penetration = radius - depth;
    if (worst && penetration <= worst.penetration) continue;
    // The direction out is resolved here rather than read off the surface,
    // because a column does not have one until somebody is standing next to it.
    worst = { surface, depth, penetration, normal: normalAt(point, surface) };
  }
  return worst;
}

/**
 * Slides a point back out of the surface it has gone into.
 *
 * With a hair to spare, because resting *exactly* on the contact threshold is a
 * coin toss in floating point — and losing that toss means registering against
 * the same wall again on the next frame, forever.
 */
const CLEARANCE = 1e-6;

export function separate(point, hit) {
  const out = hit.penetration + CLEARANCE;
  const normal = hit.normal ?? hit.surface.normal;
  return {
    x: point.x + normal.x * out,
    z: point.z + normal.z * out,
  };
}
